import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  Alert, Breadcrumbs, Button, Modal, TextInput,
} from '@opendate-app/setlist';
import CrmPageLayout from '../../shared/components/CrmPageLayout';

const axios = require('axios').default;

const NEVER_RENUMBERED = "Opendate never renumbers a contact once it has an ID, even if you change or clear the prefix later.";

const contacts = (count) => `${count.toLocaleString()} contact${count === 1 ? '' : 's'}`;
const verb = (count, singular, pluralForm) => (count === 1 ? singular : pluralForm);

const startingAt = (nextNumber) => (/^\d+$/.test(nextNumber) ? `, starting at number ${nextNumber}` : '');

const confirmationFor = (current, requestedPrefix, nextNumber) => {
  const waiting = current.prefix ? current.contactCount - current.numberedCount : 0;

  if (!current.prefix && current.previouslyEnabled) {
    return {
      title: `Turn IDs back on with ${requestedPrefix}?`,
      description: `${contacts(current.numberedCount)} already ${verb(current.numberedCount, 'has an ID and keeps it', 'have an ID and keep them')}. Contacts without one get an ID with the ${requestedPrefix} prefix${startingAt(nextNumber)}, and so does every contact added later.`,
      confirmLabel: 'Turn IDs on',
    };
  }
  if (!current.prefix) {
    const unnumbered = current.contactCount - current.numberedCount;
    return {
      title: unnumbered > 0 ? `Give ${contacts(unnumbered)} a permanent ID?` : 'Turn on contact IDs?',
      description: [
        `Every contact without an ID gets one made of ${requestedPrefix} and a number${startingAt(nextNumber)}, and every contact added later gets the next one.`,
        current.numberedCount > 0 ? `${contacts(current.numberedCount)} that already ${verb(current.numberedCount, 'has an ID keeps it', 'have an ID keep theirs')}.` : '',
        `This is a one-time choice: ${NEVER_RENUMBERED}`,
      ].filter(Boolean).join(' '),
      confirmLabel: unnumbered > 0 ? 'Give every contact an ID' : 'Turn on IDs',
    };
  }
  if (!requestedPrefix) {
    return {
      title: 'Stop giving out IDs?',
      description: [
        "New contacts won't get an ID.",
        current.numberedCount > 0 ? `${contacts(current.numberedCount)} that already ${verb(current.numberedCount, 'has one keeps it', 'have one keep them')}.` : '',
        waiting > 0 ? `${contacts(waiting)} still waiting for an ID won't get one.` : '',
      ].filter(Boolean).join(' '),
      confirmLabel: 'Stop new IDs',
    };
  }
  return {
    title: `Change the prefix to ${requestedPrefix}?`,
    description: [
      current.numberedCount > 0 ? `${contacts(current.numberedCount)} that already ${verb(current.numberedCount, 'has an ID keeps the one it has', 'have an ID keep the ones they have')}.` : '',
      waiting > 0 ? `${contacts(waiting)} still waiting for an ID will get one with the ${requestedPrefix} prefix instead.` : '',
      `Contacts added from now on get IDs with the ${requestedPrefix} prefix.`,
    ].filter(Boolean).join(' '),
    confirmLabel: 'Change prefix',
  };
};

const statusFor = (current) => {
  const waiting = current.contactCount - current.numberedCount;
  if (current.prefix && waiting > 0) {
    return {
      color: 'brand',
      title: `Giving IDs to ${contacts(waiting)}`,
      description: `This takes a few minutes on a large team. ${contacts(current.numberedCount)} ${verb(current.numberedCount, 'has', 'have')} an ID so far; reload the page to see progress. ${NEVER_RENUMBERED}`,
    };
  }
  if (current.prefix) {
    return {
      color: 'gray',
      title: `${contacts(current.numberedCount)} ${verb(current.numberedCount, 'has', 'have')} an ID`,
      description: `${NEVER_RENUMBERED} Changing the prefix only affects contacts added from now on, and clearing it stops new IDs without removing the ones contacts already have.`,
    };
  }
  if (current.previouslyEnabled) {
    return {
      color: 'gray',
      title: 'IDs are off',
      description: `${contacts(current.numberedCount)} ${verb(current.numberedCount, 'keeps the ID it was', 'keep the IDs they were')} given. New contacts won't get one until you set a prefix again.`,
    };
  }
  return {
    color: 'warning',
    title: 'You get one chance at this',
    description: `Saving a prefix gives every contact without an ID a permanent one straight away. ${NEVER_RENUMBERED} Pick the prefix and starting number you want to keep.`,
  };
};

function CrmConstituentIdsPage({ csrfToken, team, constituentIds: initialConstituentIds }) {
  const [constituentIds, setConstituentIds] = useState(initialConstituentIds);
  const [idPrefix, setIdPrefix] = useState(initialConstituentIds.prefix || '');
  const [nextNumber, setNextNumber] = useState(String(initialConstituentIds.nextNumber));
  const [confirmation, setConfirmation] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const requestedPrefix = idPrefix.trim();
  const prefixChanged = requestedPrefix !== (constituentIds.prefix || '');
  const nextNumberChanged = nextNumber !== String(constituentIds.nextNumber);
  const status = statusFor(constituentIds);

  const payload = () => {
    const teamParams = {};
    if (prefixChanged) teamParams.constituent_id_prefix = requestedPrefix;
    if (nextNumberChanged) teamParams.constituent_id_next_number = nextNumber;
    return { team: teamParams };
  };

  const persist = async () => {
    setConfirming(false);
    setSaving(true);
    try {
      const response = await axios.patch(`/teams/${team.id}/crm_constituent_ids.json`, payload(), { headers: { 'X-CSRF-TOKEN': csrfToken } });
      setConstituentIds(response.data.constituentIds);
      setIdPrefix(response.data.constituentIds.prefix || '');
      setNextNumber(String(response.data.constituentIds.nextNumber));
      toast.success('Contact IDs saved.');
    } catch (error) {
      const messages = error.response && error.response.data && error.response.data.errors;
      toast.error(Array.isArray(messages) ? messages.join(', ') : 'Failed to save contact IDs.');
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (prefixChanged) {
      setConfirmation(confirmationFor(constituentIds, requestedPrefix, nextNumber));
      setConfirming(true);
    } else {
      persist();
    }
  };

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="settings"
      mainTopMargin={32}
      breadcrumb={<Breadcrumbs items={[{ label: 'Settings', href: `/teams/${team.id}/crm-settings` }, { label: 'Contact IDs' }]} />}
      title="Contact IDs"
      subtitle={`Give every contact a permanent ${constituentIds.label}, like LUX-0001, to look people up and to match them on import.`}
    >
      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <Alert color={status.color} title={status.title} description={status.description} />
          </div>
          <div className="mb-3">
            <TextInput
              id="constituent-id-prefix"
              label="ID prefix"
              placeholder="e.g. LUX"
              maxLength={10}
              value={idPrefix}
              onChange={(e) => setIdPrefix(e.target.value.toUpperCase())}
              hint="1 to 10 letters or numbers. IDs look like PREFIX-0001."
            />
          </div>
          <div className="mb-3">
            <TextInput
              id="constituent-id-next-number"
              label="Next ID number"
              type="number"
              min={constituentIds.nextNumber}
              value={nextNumber}
              onChange={(e) => setNextNumber(e.target.value)}
              hint="The number the next ID gets. It can go up but never down. Importing IDs from another system? Set this above the highest number you'll import so new IDs never collide with them."
            />
          </div>
          <Button onClick={save} disabled={saving || (!prefixChanged && !nextNumberChanged)}>
            Save
          </Button>
        </div>
      </div>
      <Modal
        open={confirming}
        type="warning"
        icon
        title={confirmation && confirmation.title}
        description={confirmation && confirmation.description}
        confirmLabel={confirmation && confirmation.confirmLabel}
        onConfirm={persist}
        onCancel={() => setConfirming(false)}
        onClose={() => setConfirming(false)}
      />
    </CrmPageLayout>
  );
}

CrmConstituentIdsPage.propTypes = {
  csrfToken: PropTypes.string,
  team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  constituentIds: PropTypes.shape({
    prefix: PropTypes.string,
    nextNumber: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    contactCount: PropTypes.number.isRequired,
    numberedCount: PropTypes.number.isRequired,
    previouslyEnabled: PropTypes.bool.isRequired,
  }).isRequired,
};

export default CrmConstituentIdsPage;
